import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { bars, gateSourceLabel, parseGatePair, previewLine, previewMatches } from '../../lib/qualityView';
import { hubService, type GatePreview, type GateView, type NearMiss } from '../../services/hub.service';
import { hubClient } from '../../services/hubClient';

/**
 * Operator-driven tuning of this bot's no-LLM intent gate (spec 2026-10-06-intent-gate-tuning):
 * evidence from saved scores, a what-if preview, near misses to read and label, guarded save + audit.
 */
export function GateTuning({ botId, system }: { botId: string; system: 'chatbot' | 'chatbot_v2' }) {
  const { getToken } = useAuth();
  const [view, setView] = useState<GateView | null>(null);
  const [days, setDays] = useState(14);
  const [score, setScore] = useState('');
  const [margin, setMargin] = useState('');
  const [preview, setPreview] = useState<GatePreview | null>(null);
  const [misses, setMisses] = useState<NearMiss[]>([]);
  const [labelled, setLabelled] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const v = await hubService.getIntentGate(system, botId, getToken());
    setView(v);
    setScore(v.effective.minScore.toFixed(2));
    setMargin(v.effective.minMargin.toFixed(2));
  }, [system, botId, getToken]);

  useEffect(() => {
    load().catch((e) => setMsg(e instanceof Error ? e.message : 'Load failed'));
  }, [load]);

  const parsed = view ? parseGatePair(score, margin, view.bounds) : { error: 'loading' };
  const candScore = parsed.value?.minScore;
  const candMargin = parsed.value?.minMargin;

  const effScore = view?.effective.minScore;
  const effMargin = view?.effective.minMargin;

  // Refetch when the inputs, the window or the saved gate change; a response that arrives after a newer request
  // started is dropped (`alive`), and previewMatches() below hides anything that is not about what is on screen.
  useEffect(() => {
    if (candScore == null || candMargin == null) return;
    let alive = true;
    const t = setTimeout(() => {
      hubService
        .getGatePreview(botId, system, days, candScore, candMargin, getToken())
        .then((p) => alive && setPreview(p))
        .catch(() => alive && setPreview(null));
      hubService
        .getNearMisses(botId, system, candScore, getToken())
        .then((m) => alive && setMisses(m))
        .catch(() => alive && setMisses([]));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [candScore, candMargin, days, botId, system, getToken, effScore, effMargin]);

  async function run(fn: () => Promise<GateView>, done: string) {
    setBusy(true);
    try {
      const v = await fn();
      setView(v);
      setScore(v.effective.minScore.toFixed(2));
      setMargin(v.effective.minMargin.toFixed(2));
      setConfirming(false);
      setMsg(done);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function label(m: NearMiss, kind: 'intent' | 'none' | 'noise') {
    const code = m.candidateCodes?.[0];
    try {
      await hubClient.upsertGoldLabels({
        system,
        botId,
        items: [{ text: m.text, labelKind: kind, ...(kind === 'intent' && code ? { intentCode: code } : {}), ...(m.surface ? { surface: m.surface } : {}) }],
      });
      setLabelled((x) => ({ ...x, [m.text]: kind === 'intent' ? `correct: ${code}` : kind }));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Label failed');
    }
  }

  if (!view) return <div className="text-sm text-gray-500">{msg ?? 'Loading gate…'}</div>;
  const shown = previewMatches(preview, parsed.value, view.effective, days) ? preview : null;
  // Postgres `real` returns e.g. 0.7799999713897705 for 0.78 — compare with a tolerance, not !==.
  const differs = (a: number, b: number) => Math.abs(a - b) > 1e-6;
  const changed =
    parsed.value != null && (differs(parsed.value.minScore, view.effective.minScore) || differs(parsed.value.minMargin, view.effective.minMargin));

  return (
    <div className="space-y-4 rounded border border-gray-200 p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium">Gate tuning</div>
          <div className="text-xs text-gray-600">Current: {gateSourceLabel(view)}</div>
        </div>
        <button
          disabled={busy || !view.own}
          onClick={() => run(() => hubService.resetIntentGate(system, botId, getToken()), 'Reset to the platform default')}
          className="rounded border px-2 py-1 text-xs disabled:opacity-50"
        >
          Reset to default
        </button>
      </div>

      <p className="text-xs text-gray-500">
        Hub answers with an intent only when the best match scores at least the minimum and leads the runner-up by the margin; otherwise the
        message goes to the LLM / default reply. Lower = more answers and more risk of a wrong one. The preview counts how many would be
        answered, not whether they would be right — read the near misses below before lowering.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs">
          Minimum score ({view.bounds.score[0]}–{view.bounds.score[1]})
          <input type="number" step="0.01" className="w-24 rounded border px-2 py-1" value={score} onChange={(e) => setScore(e.target.value)} />
        </label>
        <label className="flex flex-col text-xs">
          Margin ({view.bounds.margin[0]}–{view.bounds.margin[1]})
          <input type="number" step="0.01" className="w-24 rounded border px-2 py-1" value={margin} onChange={(e) => setMargin(e.target.value)} />
        </label>
        <label className="flex flex-col text-xs">
          Window
          <select className="rounded border px-2 py-1" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 14, 30].map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </label>
        <button disabled={busy || !changed} onClick={() => setConfirming(true)} className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">
          Save…
        </button>
        {parsed.error && parsed.error !== 'loading' && <span className="text-xs text-red-600">{parsed.error}</span>}
        {msg && <span className="text-xs text-gray-700">{msg}</span>}
      </div>

      {shown && (
        <div className="space-y-2">
          {shown.smallSample && (
            <div className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
              Only {shown.n} scored messages in {shown.days} days — treat the preview as a hint, not evidence.
            </div>
          )}
          <div>
            {shown.n} scored messages · <span className="font-medium">{previewLine(shown)}</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Bars title="Top score" rows={bars(shown.histogram.score)} />
            <Bars title="Margin to runner-up" rows={bars(shown.histogram.margin)} />
          </div>
          {shown.byIntent.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="py-1">Intent</th>
                  <th>Now</th>
                  <th>With new values</th>
                </tr>
              </thead>
              <tbody>
                {shown.byIntent.map((r) => (
                  <tr key={r.code} className="border-b border-gray-100">
                    <td className="py-1">{r.code}</td>
                    <td>{r.current}</td>
                    <td>{r.candidate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="text-xs text-gray-500">Per-intent minimums (if any) are not reflected in the shown.</div>
        </div>
      )}

      <div>
        <div className="mb-1 font-medium">Near misses — read these before changing the score</div>
        {misses.length === 0 ? (
          <div className="text-xs text-gray-500">No Review entries in that range.</div>
        ) : (
          <ul className="space-y-1">
            {misses.map((m) => (
              <li key={`${m.surface}:${m.text}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 py-1">
                <span>{m.text}</span>
                <span className="flex items-center gap-2 text-xs text-gray-500">
                  {m.wouldAnswer && <span className="rounded bg-green-50 px-1 text-green-800">would be answered</span>}
                  {Number(m.topScore).toFixed(2)}
                  {m.candidateCodes?.[0] ? ` → ${m.candidateCodes[0]}` : ''}
                  {labelled[m.text] ? (
                    <span className="rounded bg-gray-100 px-1">{labelled[m.text]}</span>
                  ) : (
                    <>
                      {m.candidateCodes?.[0] && (
                        <button className="rounded border px-1" onClick={() => label(m, 'intent')}>
                          Correct
                        </button>
                      )}
                      <button className="rounded border px-1" onClick={() => label(m, 'none')}>
                        None
                      </button>
                      <button className="rounded border px-1" onClick={() => label(m, 'noise')}>
                        Noise
                      </button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirming && parsed.value && (
        <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs">
          <div>
            Change gate {view.effective.minScore.toFixed(2)} / {view.effective.minMargin.toFixed(2)} → {parsed.value.minScore.toFixed(2)} /{' '}
            {parsed.value.minMargin.toFixed(2)}?
          </div>
          {shown ? (
            <div>
              Over the last {shown.days} days: {previewLine(shown)}.
            </div>
          ) : (
            <div>Updating the preview…</div>
          )}
          {shown?.smallSample && <div className="text-amber-800">Small sample — the effect is uncertain.</div>}
          <div className="mt-2 flex gap-2">
            <button
              disabled={busy || !shown}
              onClick={() => run(() => hubService.setIntentGate(system, botId, parsed.value!, getToken()), 'Saved — takes effect within 30 s')}
              className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50"
            >
              Confirm
            </button>
            <button onClick={() => setConfirming(false)} className="rounded border px-3 py-1">
              Cancel
            </button>
          </div>
        </div>
      )}

      {view.history.length > 0 && (
        <div>
          <div className="mb-1 font-medium">History</div>
          <ul className="space-y-0.5 text-xs text-gray-600">
            {view.history.map((h, i) => (
              <li key={i}>
                {String(h.createdAt).slice(0, 16).replace('T', ' ')} · {h.changedBy ?? 'unknown'} ·{' '}
                {h.prevMinScore == null ? 'default' : `${h.prevMinScore.toFixed(2)} / ${h.prevMinMargin?.toFixed(2)}`} →{' '}
                {h.minScore == null ? 'default' : `${h.minScore.toFixed(2)} / ${h.minMargin?.toFixed(2)}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Bars({ title, rows }: { title: string; rows: { label: string; n: number; pct: number }[] }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-gray-600">{title}</div>
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-2 text-xs">
          <span className="w-20 text-gray-500">{r.label}</span>
          <div className="h-2 flex-1 rounded bg-gray-100">
            <div className="h-2 rounded bg-blue-500" style={{ width: `${r.pct}%` }} />
          </div>
          <span className="w-8 text-right">{r.n}</span>
        </div>
      ))}
    </div>
  );
}
